export type LegalSection = {
  title: string;
  body: string;
  bullets?: string[];
};

export type LegalPageCopy = {
  metadata: { title: string; description: string };
  title: string;
  lastUpdated: string;
  intro: string;
  sections: LegalSection[];
  contactNote: string;
};

export type CadrinoLegalCopy = {
  privacy: LegalPageCopy;
  terms: LegalPageCopy;
};

const sr: CadrinoLegalCopy = {
  privacy: {
    metadata: {
      title: 'Politika privatnosti, Cadrino ERV',
      description:
        'Kako Cadrino ERV obrađuje podatke sa marketing sajta i demo forme.',
    },
    title: 'Politika privatnosti',
    lastUpdated: 'Poslednja izmena: maj 2026. (potrebna pravna provera pre objave.)',
    intro:
      'Ova politika opisuje kako se obrađuju lični podaci koje nam dostavite preko sajta cadrinoerv.com i demo forme. Za korišćenje samog proizvoda Cadrino ERV važe posebni ugovorni uslovi sa vašom firmom.',
    sections: [
      {
        title: '1. Ko obrađuje podatke',
        body:
          'Rukovalac podacima iz demo forme je provajder usluge Cadrino ERV (kontakt: contact@cadrinoerv.com). Tačan pravni subjekt i adresa biće dopunjeni nakon interne pravne potvrde.',
      },
      {
        title: '2. Koje podatke prikupljamo',
        body: 'Preko demo forme možemo primiti:',
        bullets: [
          'ime i prezime',
          'e-poštu i telefon',
          'naziv kompanije',
          'opseg zaposlenih, lokacija i uređaja',
          'trenutno rešenje koje koristite',
          'dodatnu poruku koju napišete',
          'jezik sajta koji ste izabrali',
        ],
      },
      {
        title: '3. Svrha obrade',
        body:
          'Podatke koristimo isključivo da odgovorimo na vaš upit, zakažemo demo razgovor i pripremimo informacije relevantne za vašu firmu. Ne koristimo ih za masovni marketing bez vaše saglasnosti.',
      },
      {
        title: '4. Pravni osnov i čuvanje',
        body:
          'Obrada se zasniva na vašem zahtevu pre demo razgovora i legitimnom interesu da odgovorimo na poslovni upit. Podatke čuvamo onoliko koliko je potrebno za komunikaciju i evidenciju upita, a zatim brišemo ili anonimizujemo u skladu sa internom politikom (rok će biti potvrđen pravnim timom).',
      },
      {
        title: '5. Kome možemo proslediti podatke',
        body:
          'Podatke ne prodajemo. Možemo ih podeliti sa pouzdanim obrađivačima (npr. hosting, e-pošta) koji pomažu u slanju odgovora na vaš upit, uz ugovorenu zaštitu podataka.',
      },
      {
        title: '6. Vaša prava',
        body:
          'U skladu sa važećim propisima možete zatražiti pristup, ispravku, brisanje ili ograničenje obrade, kao i prigovor. Za zahteve pišite na contact@cadrinoerv.com.',
      },
      {
        title: '7. Kolačići i analitika',
        body:
          'Sajt može koristiti tehničke kolačiće i, ako uvedemo analitiku, bićete obavešteni kroz ažuriranu verziju ove politike.',
      },
    ],
    contactNote: 'Pitanja: contact@cadrinoerv.com',
  },
  terms: {
    metadata: {
      title: 'Uslovi korišćenja sajta, Cadrino ERV',
      description: 'Uslovi korišćenja marketing sajta cadrinoerv.com.',
    },
    title: 'Uslovi korišćenja sajta',
    lastUpdated: 'Poslednja izmena: maj 2026. (potrebna pravna provera pre objave.)',
    intro:
      'Ovi uslovi regulišu korišćenje marketing sajta cadrinoerv.com. Korišćenje proizvoda Cadrino ERV kao softvera uređuje poseban ugovor sa vašom firmom.',
    sections: [
      {
        title: '1. Prihvatanje',
        body:
          'Korišćenjem sajta potvrđujete da ste pročitali ove uslove. Ako se ne slažete, nemojte koristiti sajt.',
      },
      {
        title: '2. Svrha sajta',
        body:
          'Sajt služi za informisanje o proizvodu Cadrino ERV i zakazivanje demo razgovora. Sadržaj je informativnog karaktera i ne predstavlja obavezujuću ponudu dok se ne potpiše ugovor.',
      },
      {
        title: '3. Intelektualna svojina',
        body:
          'Tekstovi, dizajn i materijali na sajtu pripadaju Cadrino brendu ili su korišćeni uz dozvolu. Zabranjeno je neovlašćeno kopiranje za komercijalnu upotrebu.',
      },
      {
        title: '4. Tačnost informacija',
        body:
          'Trudimo se da informacije budu tačne i ažurne, ali funkcionalnosti proizvoda mogu da se menjaju. Za konkretan obim usluge važi ono što dogovorimo na demo razgovoru i u ugovoru.',
      },
      {
        title: '5. Ograničenje odgovornosti',
        body:
          'Sajt se pruža „kakav jeste“. Ne odgovaramo za indirektnu štetu nastalu korišćenjem sajta ili privremenom nedostupnošću, u meri dozvoljenoj zakonom.',
      },
      {
        title: '6. Spoljni linkovi',
        body:
          'Sajt može sadržati linkove ka drugim sajtovima (npr. Cadrino HR). Za njihov sadržaj ne snosimo odgovornost.',
      },
      {
        title: '7. Izmene',
        body:
          'Uslove možemo ažurirati. Datum izmene biće naveden na vrhu stranice.',
      },
    ],
    contactNote: 'Kontakt: contact@cadrinoerv.com',
  },
};

const en: CadrinoLegalCopy = {
  privacy: {
    metadata: {
      title: 'Privacy policy, Cadrino ERV',
      description: 'How Cadrino ERV processes data from the marketing site and demo form.',
    },
    title: 'Privacy policy',
    lastUpdated: 'Last updated: May 2026. (Legal review required before publication.)',
    intro:
      'This policy describes how we process personal data you submit via cadrinoerv.com and the demo form. Use of the Cadrino ERV product itself is governed by separate contractual terms with your company.',
    sections: [
      {
        title: '1. Data controller',
        body:
          'The controller for demo form data is the Cadrino ERV service provider (contact: contact@cadrinoerv.com). The exact legal entity and address will be added after internal legal confirmation.',
      },
      {
        title: '2. Data we collect',
        body: 'Via the demo form we may receive:',
        bullets: [
          'first and last name',
          'email and phone',
          'company name',
          'employee, location, and device counts',
          'your current solution',
          'an optional message',
          'your selected site language',
        ],
      },
      {
        title: '3. Purpose',
        body:
          'We use data only to respond to your inquiry, schedule a demo, and prepare information relevant to your company. We do not use it for bulk marketing without your consent.',
      },
      {
        title: '4. Legal basis and retention',
        body:
          'Processing is based on your demo request and our legitimate interest in responding to a business inquiry. We keep data only as long as needed for communication and inquiry records, then delete or anonymize per internal policy (retention period to be confirmed by legal).',
      },
      {
        title: '5. Sharing',
        body:
          'We do not sell data. We may share it with trusted processors (e.g. hosting, email) who help us respond, under data protection agreements.',
      },
      {
        title: '6. Your rights',
        body:
          'Under applicable law you may request access, correction, deletion, or restriction, or object to processing. Contact contact@cadrinoerv.com.',
      },
      {
        title: '7. Cookies and analytics',
        body:
          'The site may use technical cookies and, if we add analytics, this policy will be updated accordingly.',
      },
    ],
    contactNote: 'Questions: contact@cadrinoerv.com',
  },
  terms: {
    metadata: {
      title: 'Website terms, Cadrino ERV',
      description: 'Terms of use for the cadrinoerv.com marketing site.',
    },
    title: 'Website terms of use',
    lastUpdated: 'Last updated: May 2026. (Legal review required before publication.)',
    intro:
      'These terms govern use of the cadrinoerv.com marketing site. Use of Cadrino ERV software is governed by a separate agreement with your company.',
    sections: [
      {
        title: '1. Acceptance',
        body: 'By using the site you confirm you have read these terms. If you disagree, do not use the site.',
      },
      {
        title: '2. Purpose',
        body:
          'The site informs about Cadrino ERV and lets you request a demo. Content is informational and is not a binding offer until a contract is signed.',
      },
      {
        title: '3. Intellectual property',
        body:
          'Site content and design belong to the Cadrino brand or are used with permission. Unauthorized commercial copying is prohibited.',
      },
      {
        title: '4. Accuracy',
        body:
          'We aim for accurate, up-to-date information, but product capabilities may change. Scope of service is defined in the demo conversation and contract.',
      },
      {
        title: '5. Limitation of liability',
        body:
          'The site is provided as is. We are not liable for indirect damage from use or temporary unavailability, to the extent permitted by law.',
      },
      {
        title: '6. External links',
        body:
          'The site may link to other sites (e.g. Cadrino HR). We are not responsible for their content.',
      },
      {
        title: '7. Changes',
        body: 'We may update these terms. The date will appear at the top of this page.',
      },
    ],
    contactNote: 'Contact: contact@cadrinoerv.com',
  },
};

export const cadrinoLegalCopy = { sr, en };

export function getCadrinoLegalCopy(language: string): CadrinoLegalCopy {
  return cadrinoLegalCopy[language === 'en' ? 'en' : 'sr'];
}
